import React, { memo } from 'react';
import styled, { css } from 'styled-components/macro';
import { hideVisually } from 'polished';

import colors from 'styles/themes/colors';
import theme from 'styles/themes/variables';

import { ReactComponent as ZoomInIcon } from 'icons/icon-plus.svg';
import { ReactComponent as ZoomOutIcon } from 'icons/icon-minus.svg';
import { ReactComponent as ResetZoomIcon } from 'icons/icon-reset.svg';

const ControlsWrapper = styled.div`
  position: absolute;
  right: ${theme.spacing.small};
  top: 50%;
  transform: translate3d(0, -50%, 0);
`;

const stripe = css`
  &:first-of-type {
    position: relative;

    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 20%;
      width: 60%;
      height: 1px;
      background: ${colors.LightGrey};
    }
  }
`;

const interactivityStyles = css`
  &:hover {
    svg {
      color: ${colors.AccentGreen};
    }
  }
  &:focus {
    background: ${colors.Sand};
  }
  &:active {
    svg {
      color: ${colors.AccentGreen};
    }
  }
`;

interface IButton {
  adjacent: boolean;
  active: boolean;
}

const Button = styled.button`
  appearance: none;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 0;
  background: white;

  ${({ adjacent, active }: { adjacent: boolean; active: boolean }): any => css<
    IButton
  >`
    ${active && interactivityStyles}
    ${adjacent && stripe}

    svg {
      color: ${active ? colors.DeepBlue : colors.CasperGrey};
    }
  `};

  &[disabled] {
    pointer-events: none;

    svg {
      color: ${colors.CasperGrey};
    }
  }
`;

const ButtonContent = styled.span`
  display: flex;
  user-select: none;
  padding: ${theme.spacing.xxsmall};
`;

const ResetButton = styled(Button)<any>`
  margin-top: ${theme.spacing.xxsmall};
  border: 1px solid ${colors.LightGrey};
`;

const ZoomInOut = styled.div`
  display: flex;
  flex-flow: column nowrap;
  border: 1px solid ${colors.LightGrey};
`;

const Text = styled.span`
  ${hideVisually()};
`;

/**
 * ZoomControls renders three zoom buttons in a SVG parent.
 *
 * @method      ZoomControls
 * @param       {Object}  props React props
 * @param       {Number}  props.currentZoom The current zoom transform scale
 * @param       {Function}  props.zoomIn Function called to zoom in
 * @param       {Function}  props.zoomOut Function called to zoom out
 * @param       {Function}  props.resetZoom Function called to reset the zoom level
 * @param       {Array}  props.zoomExtent The min and max zoom level
 * @constructor
 */

interface IZoomControlsProps {
  currentZoom: number;
  disabled: boolean;
  zoomExtent: number[];
  zoomIn: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  zoomOut: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  resetZoom: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const ZoomControls: React.FC<IZoomControlsProps> = (props) => {
  const {
    currentZoom,
    zoomExtent,
    zoomIn,
    zoomOut,
    resetZoom,
    disabled,
  } = props;

  const [minZoom, maxZoom] = zoomExtent;
  const zoomInActive = currentZoom < maxZoom;
  const zoomOutActive = currentZoom > minZoom;

  return (
    <ControlsWrapper>
      <ZoomInOut>
        <Button
          onClick={zoomIn}
          active={zoomInActive}
          adjacent
          disabled={disabled}
        >
          <ButtonContent>
            <ZoomInIcon /> <Text>Zoom in</Text>
          </ButtonContent>
        </Button>
        <Button
          onClick={zoomOut}
          active={zoomOutActive}
          adjacent
          disabled={disabled}
        >
          <ButtonContent>
            <ZoomOutIcon /> <Text>Zoom out</Text>
          </ButtonContent>
        </Button>
      </ZoomInOut>
      <ResetButton onClick={resetZoom} active disabled={disabled}>
        <ButtonContent>
          <ResetZoomIcon /> <Text>Reset the zoom level</Text>
        </ButtonContent>
      </ResetButton>
    </ControlsWrapper>
  );
};

export default memo(ZoomControls);
